import {
  terminalParamValueMatchesType,
  type TerminalParamDescriptor,
  type TerminalParamValue,
} from '@reform-lite/contracts';

/**
 * Value helpers for the registry-driven parameter editor (RL-272, TP-FR-012).
 *
 * Everything here is derived from the descriptor alone. There is no per-key
 * table: a key the registry adds tomorrow formats, parses and validates
 * through the same functions as the thirty-six it ships with today.
 *
 * Money and percent stay strings end to end. `money` is integer minor units
 * (`"5000000"` is 50 000.00) and the only conversion is textual — split at
 * the decimal point, pad, join — so a value can never pass through a float
 * on its way to a fiscal receipt.
 */

/** Minor-unit exponent for the money display. Every supported currency has two. */
const MONEY_DECIMALS = 2;

/** `"5000000"` → `"50000.00"`, `"-5"` → `"-0.05"`. Pure string arithmetic. */
export function minorToMajor(minor: string): string {
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).replace(/^0+(?=\d)/, '');
  const padded = digits.padStart(MONEY_DECIMALS + 1, '0');
  const whole = padded.slice(0, -MONEY_DECIMALS);
  const fraction = padded.slice(-MONEY_DECIMALS);
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/**
 * `"50000"` / `"50000.00"` / `"50000,5"` → `"5000000"`.
 *
 * Accepts a comma as the decimal separator because that is what a Lithuanian
 * or Ukrainian keyboard produces. More than two decimals is refused rather
 * than rounded: a rounding rule would be a silent edit of a fiscal figure.
 */
export function majorToMinor(major: string): { value: string } | { error: string } {
  const text = major.trim().replace(',', '.');
  const match = /^(-?)(\d+)(?:\.(\d{0,2}))?$/.exec(text);
  if (match === null) {
    return { error: 'Enter an amount such as 12.50, with at most two decimals.' };
  }
  const [, sign, whole, fraction = ''] = match;
  const minor = `${whole}${fraction.padEnd(MONEY_DECIMALS, '0')}`.replace(/^0+(?=\d)/, '');
  return { value: minor === '0' ? '0' : `${sign}${minor}` };
}

/**
 * The four words `formatParamValue` can render in place of a value. Passed in
 * rather than looked up, because `useT` is a hook and this module is pure.
 */
export type ValueWords = {
  true: string;
  false: string;
  emptyList: string;
  empty: string;
  /** MICRO-10: what a password parameter reads as — never its value. */
  secretSet: string;
  secretNotSet: string;
};

export const VALUE_WORDS_EN: ValueWords = {
  true: 'true',
  false: 'false',
  emptyList: '(empty list)',
  empty: '(empty)',
  secretSet: 'password set',
  secretNotSet: 'no password',
};

/** Resolve the value words for the operator's locale. Keys are literal here so
 * the parity guard can see them; the second argument is the baked English. */
export function valueWords(t: (key: string, bakedEnglish?: string) => string): ValueWords {
  return {
    true: t('parameters.vocab.valueTrue', 'true'),
    false: t('parameters.vocab.valueFalse', 'false'),
    emptyList: t('parameters.vocab.valueEmptyList', '(empty list)'),
    empty: t('parameters.vocab.valueEmpty', '(empty)'),
    secretSet: t('parameters.vocab.valueSecretSet', 'password set'),
    secretNotSet: t('parameters.vocab.valueSecretNotSet', 'no password'),
  };
}

/**
 * Operator-facing rendering of a value, for summaries and the clear outcome.
 * `words` defaults to the English the editor has always shown, so a caller that
 * passes nothing is unaffected.
 */
export function formatParamValue(
  descriptor: Pick<TerminalParamDescriptor, 'type'> &
    Partial<Pick<TerminalParamDescriptor, 'secret'>>,
  value: TerminalParamValue | undefined,
  words: ValueWords = VALUE_WORDS_EN,
): string {
  if (value === undefined) return '—';
  // MICRO-10: the API sends a placeholder for a stored password; whatever
  // arrives, a secret is only ever "set" or "not set" on screen.
  if (descriptor.secret !== undefined) return value === '' ? words.secretNotSet : words.secretSet;
  switch (descriptor.type) {
    case 'bool':
      return value === true ? words.true : words.false;
    case 'money':
      return typeof value === 'string' && /^-?\d+$/.test(value)
        ? minorToMajor(value)
        : String(value);
    case 'percent':
      return `${String(value)} %`;
    case 'durationSeconds':
      return `${String(value)} s`;
    case 'stringList':
      return Array.isArray(value) ? (value.length === 0 ? words.emptyList : value.join(', ')) : '—';
    case 'string':
      return value === '' ? words.empty : String(value);
    default:
      return String(value);
  }
}

/**
 * The reason a draft cannot be saved, or `null` when it can. Mirrors what the
 * API refuses — type, enum membership, integer and money bounds — so the
 * operator hears it before the round trip rather than after.
 */
export function validateParamValue(
  descriptor: TerminalParamDescriptor,
  value: TerminalParamValue,
): string | null {
  if (!terminalParamValueMatchesType(descriptor.type, value, descriptor.enumValues)) {
    return typeMessage(descriptor);
  }
  if (typeof value === 'number') {
    if (descriptor.minInt !== undefined && value < descriptor.minInt) {
      return `Must be at least ${descriptor.minInt}.`;
    }
    if (descriptor.maxInt !== undefined && value > descriptor.maxInt) {
      return `Must be at most ${descriptor.maxInt}.`;
    }
  }
  if (descriptor.type === 'money' && typeof value === 'string') {
    const money = BigInt(value);
    if (descriptor.minMoney !== undefined && money < BigInt(descriptor.minMoney)) {
      return `Must be at least ${minorToMajor(descriptor.minMoney)}.`;
    }
    if (descriptor.maxMoney !== undefined && money > BigInt(descriptor.maxMoney)) {
      return `Must be at most ${minorToMajor(descriptor.maxMoney)}.`;
    }
  }
  return null;
}

function typeMessage(descriptor: TerminalParamDescriptor): string {
  switch (descriptor.type) {
    case 'bool':
      return 'Must be on or off.';
    case 'int':
      return 'Must be a whole number.';
    case 'durationSeconds':
      return 'Must be a whole number of seconds, zero or more.';
    case 'money':
      return 'Must be an amount such as 12.50.';
    case 'percent':
      return 'Must be a percentage from 0 to 100, with at most two decimals.';
    case 'timeOfDay':
      return 'Must be a 24-hour time such as 23:50.';
    case 'enum':
      return `Must be one of: ${(descriptor.enumValues ?? []).join(', ')}.`;
    case 'stringList':
      return 'Must be a list of text entries.';
    case 'string':
      return 'Must be text.';
  }
}

/**
 * Catalogue keys for the registry groups that exist today. This is not the
 * per-key table TP-FR-011 rules out: `groupLabel` still derives the English from
 * the group token, and a group the registry adds tomorrow renders through that
 * derivation with no UI change — untranslated, but present, exactly as now.
 * Literal keys, because the parity guard reads source for literal keys.
 */
export const GROUP_LABEL_KEY: Record<string, string> = {
  confirmation: 'parameters.vocab.groupConfirmation',
  discount: 'parameters.vocab.groupDiscount',
  fiscal: 'parameters.vocab.groupFiscal',
  kitchen: 'parameters.vocab.groupKitchen',
  lifecycle: 'parameters.vocab.groupLifecycle',
  loyalty: 'parameters.vocab.groupLoyalty',
  payment: 'parameters.vocab.groupPayment',
  pos: 'parameters.vocab.groupPos',
  pricing: 'parameters.vocab.groupPricing',
  receipt: 'parameters.vocab.groupReceipt',
  returns: 'parameters.vocab.groupReturns',
  sco: 'parameters.vocab.groupSco',
  shift: 'parameters.vocab.groupShift',
  tender: 'parameters.vocab.groupTender',
  ui: 'parameters.vocab.groupUi',
};

/**
 * Display label for a registry group. Derived, not tabled, so a new group
 * appears without a UI change (TP-FR-011). Short acronym-like groups such as
 * `pos` read better upper-cased. Pass `useT`'s translator to get the operator's
 * language; without it the derived English is returned, as before.
 */
export function groupLabel(
  group: string,
  t?: (key: string, bakedEnglish?: string) => string,
): string {
  const english =
    group.length <= 3 ? group.toUpperCase() : group.charAt(0).toUpperCase() + group.slice(1);
  const key = GROUP_LABEL_KEY[group];
  return t !== undefined && key !== undefined ? t(key, english) : english;
}

/** Registry group order: the order groups first appear in the registry. */
export function groupDescriptors(
  descriptors: readonly TerminalParamDescriptor[],
): { group: string; descriptors: TerminalParamDescriptor[] }[] {
  const groups = new Map<string, TerminalParamDescriptor[]>();
  for (const descriptor of descriptors) {
    const group = descriptor.group ?? descriptor.key.split('.')[0] ?? descriptor.key;
    const bucket = groups.get(group);
    if (bucket === undefined) groups.set(group, [descriptor]);
    else bucket.push(descriptor);
  }
  return [...groups.entries()].map(([group, entries]) => ({ group, descriptors: entries }));
}
